/**
 * Admin-editable UI texts.
 *
 * On app boot `UIProvider` fetches `/api/settings/ui-texts` once and keeps the
 * overrides in a React context + localStorage so pages render instantly on
 * repeat visits. `useUIText(key, fallback)` returns the current value or the
 * built-in Arabic fallback.
 */
import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const STORAGE_KEY = "kh_ui_texts";
const UITextContext = createContext({});


export function UIProvider({ children }) {
  const [overrides, setOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); }
    catch { return {}; }
  });

  useEffect(() => {
    api.get("/settings/ui-texts").then((r) => {
      const next = r.data || {};
      setOverrides(next);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* quota */ }
    }).catch(() => {});
  }, []);

  return (
    <UITextContext.Provider value={overrides}>
      {children}
    </UITextContext.Provider>
  );
}


/** Returns the current override for `key`, or `fallback` if not set. */
export function useUIText(key, fallback) {
  const overrides = useContext(UITextContext) || {};
  const v = overrides[key];
  return (typeof v === "string" && v.trim()) ? v : fallback;
}
